import { countryFor, DIAL_CODES_LONGEST_FIRST, primaryCountryForDialCode, } from './countries';
/**
 * E.164 helpers: split a stored number into (country, national digits) for
 * display, and put it back together for emission.
 *
 * Scope on purpose: this knows the shape of E.164 (a `+`, then at most 15
 * digits, the first of which is not zero) and the set of country calling codes.
 * It knows nothing about national numbering plans and never will — whether
 * `+14155551234` is a real, reachable line is the consumer's Zod schema's
 * question, and a hand-rolled per-country length table would go stale silently.
 */
/** ITU-T E.164: the whole number, country code included, is at most 15 digits. */
export const E164_MAX_DIGITS = 15;
/** `+`, a non-zero leading digit, then up to 14 more. Nothing else — no spaces,
 *  no separators, no extension. */
const E164_PATTERN = /^\+[1-9]\d{0,14}$/;
/** Every digit in `value`, in order. Anything else — `+`, spaces, parentheses,
 *  dashes, letters — is dropped, which is what makes a pasted, formatted number
 *  land correctly. */
export function digitsOf(value) {
    return value.replace(/\D/gu, '');
}
/**
 * Split a well-formed E.164 string. Returns `null` — never a guess — when the
 * value is empty, is not E.164-shaped, or starts with digits no country dials.
 * Callers must handle that: the honest rendering of an unparseable value is not
 * a wrong flag.
 */
export function parseE164(value) {
    if (!E164_PATTERN.test(value))
        return null;
    const digits = value.slice(1);
    for (const dialCode of DIAL_CODES_LONGEST_FIRST) {
        if (!digits.startsWith(dialCode))
            continue;
        const country = primaryCountryForDialCode(dialCode);
        if (country === null)
            continue;
        return { country, dialCode, nationalNumber: digits.slice(dialCode.length) };
    }
    return null;
}
/**
 * Build the E.164 candidate for a country and whatever the user has typed.
 *
 * Returns `''` for an empty national part rather than a bare `+49`: a dial code
 * with no number behind it is not a phone number, and emitting one would make a
 * consumer's `z.string().optional()` see a value where the user entered none.
 * The result is capped at `E164_MAX_DIGITS` total, so the field cannot produce a
 * string that is out of spec on length no matter how long the paste was.
 */
export function toE164(dialCode, nationalPart) {
    const room = Math.max(0, E164_MAX_DIGITS - dialCode.length);
    const digits = digitsOf(nationalPart).slice(0, room);
    return digits === '' ? '' : `+${dialCode}${digits}`;
}
/** The same, keyed by ISO code — the form `PhoneInput` and most consumers want. */
export function toE164ForCountry(code, nationalPart) {
    return toE164(countryFor(code).dialCode, nationalPart);
}
/** Whether `value` is a well-formed E.164 string whose country code exists.
 *  A shape check, deliberately NOT a reachability check. */
export function isE164Candidate(value) {
    return parseE164(value) !== null;
}
//# sourceMappingURL=e164.js.map